/**
 * Pulsante campanella nell'intestazione del drawer Messaggi: accende/spegne
 * gli avvisi dei nuovi messaggi (salvato per utente) e, con un clic esplicito,
 * chiede al browser il permesso per le notifiche di sistema.
 */
import { useEffect, useRef, useState } from 'react';
import { Bell, BellOff } from 'lucide-react';
import { iconTip } from '@/components/messages/ChatTooltip';
import type { BrowserPermission } from './useIncomingMessageNotifications';

interface Props {
  enabled: boolean;
  onToggle: (value: boolean) => void;
  permission: BrowserPermission;
  onRequestPermission: () => void;
}

export function NotificationToggle({ enabled, onToggle, permission, onRequestPermission }: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="p-1.5 rounded-md hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 text-[#5F5E5B] dark:text-white/70 transition-colors"
        {...iconTip(enabled ? 'Avvisi nuovi messaggi: accesi' : 'Avvisi nuovi messaggi: spenti', 'bottom')}
      >
        {enabled ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
      </button>
      {open && (
        <div role="dialog" aria-label="Avvisi nuovi messaggi"
          className="absolute right-0 top-full mt-1 z-50 w-72 p-3 rounded-lg bg-white dark:bg-[#252525] border border-[rgba(55,53,47,0.12)] dark:border-white/10 shadow-lg">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input type="checkbox" checked={enabled} onChange={(e) => onToggle(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#2383E2]" />
            <span>
              <span className="block text-sm font-medium text-[#37352F] dark:text-white">Avvisami dei nuovi messaggi</span>
              <span className="block text-xs text-[#5F5E5B] dark:text-white/70 mt-0.5">
                Un suono leggero quando arriva un messaggio WhatsApp, Instagram o Messenger e i messaggi sono chiusi
                o stai lavorando in un'altra scheda.
              </span>
            </span>
          </label>
          <div className="mt-3 pt-3 border-t border-[rgba(55,53,47,0.09)] dark:border-white/10 text-xs text-[#5F5E5B] dark:text-white/70">
            {permission === 'granted' && 'Le notifiche del browser sono attive.'}
            {permission === 'denied' && 'Le notifiche del browser sono bloccate: puoi riattivarle dalle impostazioni del sito nel browser.'}
            {permission === 'unsupported' && 'Questo browser non supporta le notifiche.'}
            {permission === 'default' && (
              <>
                <p>Vuoi vedere anche un avviso del computer, con il nome di chi ti ha scritto?</p>
                <button
                  onClick={onRequestPermission}
                  disabled={!enabled}
                  className="mt-2 px-3 py-1.5 text-xs font-semibold text-white bg-[#2383E2] hover:bg-[#1a6fc2] disabled:opacity-50 rounded-md"
                >
                  Attiva le notifiche del browser
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
